import * as React from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeftRight,
  ArrowRight,
  Check,
  CheckCircle2,
  ClipboardPaste,
  Copy,
  Download,
  History,
  Plus,
  RefreshCw,
  Repeat,
  ScanLine,
  Undo2,
  Upload,
  X,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useSessao } from '@/hooks/useSessao';
import { useDebounce } from '@/hooks/useDebounce';
import { useParamUrl } from '@/hooks/useUrlEstado';
import { cn, numero } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { Cartao, Esqueleto } from '@/componentes/ui/primitivos';
import { Segmentado } from '@/componentes/ui/segmentado';
import { lerLote, MAX_LINHAS_LOTE, MODELO_CSV, type LinhaLote } from './lerListaLocalizador';
import { CampoLocalizador, Localizador, type Posicao } from './Localizador';

/**
 * Transferência entre LOCALIZADORES do mesmo centro, em lote.
 *
 * É o formulário TRANSFERECIA_LOCALIZADORES do Procfit com outra cara: mesmo
 * centro, sem aprovação, grava cabeçalho, itens e kardex de uma vez.
 *
 * A tela é uma lista de texto só. Bipar, colar do Excel e abrir um .csv
 * escrevem na mesma caixa; a prévia confere cada linha no servidor (produto,
 * localizador, saldo na origem somando as linhas) e o botão só libera quando
 * tudo passa. Nada é gravado até a confirmação.
 *
 * Localizador é sempre o CÓDIGO — a descrição se repete no cadastro, o código
 * não. A descrição aparece ao lado, para a pessoa conferir com o olho.
 */

interface Candidato {
  produto: number;
  descricao: string | null;
  via: 'codigo' | 'ean' | 'lote';
}

interface LinhaConferida {
  linha: number;
  codigo: string;
  quantidade: number;
  produto: Candidato | null;
  candidatos: Candidato[];
  origem: Posicao | null;
  destino: Posicao | null;
  saldoOrigem: number | null;
  totalNaOrigem: number;
  impedimentos: string[];
  avisos: string[];
}

interface RespostaConferir {
  linhas: LinhaConferida[];
  padroes: { origem: Posicao | string | null; destino: Posicao | string | null };
}

interface Resumo {
  transferencia: number;
  data: string | null;
  observacao: string | null;
  itens: number;
  unidades: number;
  usuario: number | null;
  usuarioNome: string | null;
}

interface ItemDetalhe {
  item: number;
  produto: number;
  descricao: string | null;
  ean: string | null;
  quantidade: number;
  origem: number;
  origemEndereco: string | null;
  destino: number;
  destinoEndereco: string | null;
}

interface Detalhe extends Resumo {
  centro: number;
  lancamentos: number;
  itensDetalhe: ItemDetalhe[];
  importacao: { arquivo: string | null; texto: string; em: string; login: string | null } | null;
}

/** Pedido de carregar uma lista no formulário (vem do histórico). */
interface Rascunho {
  texto: string;
  chave: number;
}

function dataHora(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function baixar(nome: string, texto: string): void {
  const url = URL.createObjectURL(new Blob([texto], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  a.click();
  URL.revokeObjectURL(url);
}

/** TXT do Windows costuma vir em ANSI; UTF-8 primeiro, senão Windows-1252. */
async function lerArquivo(f: File): Promise<string> {
  const bytes = await f.arrayBuffer();
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

export function TransferenciaLocalizadores({
  centro,
  centroNome,
  controlaLocalizador,
}: {
  centro: number | null;
  centroNome: string | null;
  controlaLocalizador: boolean;
}): JSX.Element {
  const [abaUrl, definirAbaUrl] = useParamUrl('lista');
  const aba = abaUrl === 'historico' ? 'historico' : 'nova';
  const [abertaUrl, definirAbertaUrl] = useParamUrl('tl');
  const aberta = Number(abertaUrl) || null;
  const [rascunho, definirRascunho] = React.useState<Rascunho | null>(null);

  if (centro === null) {
    return (
      <Cartao className="grid min-h-[240px] place-items-center px-6 text-center">
        <p className="text-[13px] text-textoFraco">Escolha um centro de estoque no seletor do topo.</p>
      </Cartao>
    );
  }

  // Centro sem localizador não tem posição: dizer antes de a pessoa montar a lista.
  if (!controlaLocalizador) {
    return (
      <Cartao className="grid min-h-[240px] place-items-center px-6 text-center">
        <p className="text-[13px] text-textoFraco">
          O centro {centro}
          {centroNome ? ` · ${centroNome}` : ''} não controla localizador — não há posição para
          transferir. Troque o centro no seletor do topo.
        </p>
      </Cartao>
    );
  }

  return (
    <div className="space-y-3">
      <Segmentado
        tamanho="pequeno"
        valor={aba}
        aoMudar={(v) => definirAbaUrl(v === 'historico' ? 'historico' : null)}
        rotuloAcessivel="Transferência em lote"
        opcoes={[
          { valor: 'nova', rotulo: 'Nova transferência', icone: <Plus className="h-3.5 w-3.5" /> },
          { valor: 'historico', rotulo: 'Histórico', icone: <History className="h-3.5 w-3.5" /> },
        ]}
      />
      {aba === 'nova' ? (
        <FormularioLote
          centro={centro}
          centroNome={centroNome}
          rascunho={rascunho}
          aoVerHistorico={(n) => {
            definirAbertaUrl(String(n));
            definirAbaUrl('historico');
          }}
        />
      ) : (
        <HistoricoLote
          centro={centro}
          aberta={aberta}
          abrir={(n) => definirAbertaUrl(n === null ? null : String(n))}
          usarLista={(texto) => {
            definirRascunho({ texto, chave: Date.now() });
            definirAbaUrl(null);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nova transferência
// ---------------------------------------------------------------------------

function FormularioLote({
  centro,
  centroNome,
  rascunho,
  aoVerHistorico,
}: {
  centro: number;
  centroNome: string | null;
  rascunho: Rascunho | null;
  aoVerHistorico: (transferencia: number) => void;
}): JSX.Element {
  const cliente = useQueryClient();
  const { temPermissao } = useSessao();
  const podeMover = temPermissao('estoque.mover');

  const [origem, definirOrigem] = React.useState('');
  const [destino, definirDestino] = React.useState('');
  const [texto, definirTexto] = React.useState('');
  const [arquivo, definirArquivo] = React.useState<string | null>(null);
  const [observacao, definirObservacao] = React.useState('');
  /** Linha → produto escolhido, para código ambíguo. */
  const [escolhas, definirEscolhas] = React.useState<Record<number, number>>({});
  const [descartadas, definirDescartadas] = React.useState<Set<number>>(new Set());
  const [confirmando, definirConfirmando] = React.useState(false);
  const [aviso, definirAviso] = React.useState<string | null>(null);
  const [erro, definirErro] = React.useState<string | null>(null);
  const [gravada, definirGravada] = React.useState<{
    transferencia: number;
    itens: number;
    unidades: number;
  } | null>(null);

  const [bipe, definirBipe] = React.useState('');
  const [bipeQtde, definirBipeQtde] = React.useState('1');
  const campoBipe = React.useRef<HTMLInputElement>(null);
  const seletorArquivo = React.useRef<HTMLInputElement>(null);

  // Lista vinda do histórico ("repetir" / "montar o inverso").
  React.useEffect(() => {
    if (!rascunho) return;
    definirTexto(rascunho.texto);
    definirArquivo(null);
    definirGravada(null);
    definirConfirmando(false);
  }, [rascunho]);

  const textoAtrasado = useDebounce(texto, 350);
  const origemAtrasada = useDebounce(origem.trim(), 350);
  const destinoAtrasado = useDebounce(destino.trim(), 350);
  const lidas = React.useMemo(() => lerLote(textoAtrasado), [textoAtrasado]);
  const grande = lidas.length > MAX_LINHAS_LOTE;

  // Texto novo, decisões novas: a escolha da linha 7 não vale para outra lista.
  React.useEffect(() => {
    definirEscolhas({});
    definirDescartadas(new Set());
    definirConfirmando(false);
  }, [textoAtrasado]);

  const pedidas = React.useMemo(
    () =>
      lidas
        .filter((l) => !l.cabecalho && !l.problema && !descartadas.has(l.linha))
        .map((l) => ({
          linha: l.linha,
          codigo: l.codigo!,
          produto: escolhas[l.linha] ?? null,
          origem: l.origem ?? origemAtrasada,
          destino: l.destino ?? destinoAtrasado,
          quantidade: l.quantidade!,
        })),
    [lidas, descartadas, escolhas, origemAtrasada, destinoAtrasado],
  );

  const conferir = useQuery<RespostaConferir>({
    queryKey: ['lote-localizador-conferir', centro, pedidas, origemAtrasada, destinoAtrasado],
    queryFn: () =>
      api.post<RespostaConferir>('/api/transferencias-localizador/conferir', {
        centro,
        linhas: pedidas,
        padroes: { origem: origemAtrasada, destino: destinoAtrasado },
      }),
    enabled: !grande && (pedidas.length > 0 || origemAtrasada !== '' || destinoAtrasado !== ''),
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });

  const digitando =
    texto !== textoAtrasado || origem.trim() !== origemAtrasada || destino.trim() !== destinoAtrasado;
  const conferindo = digitando || conferir.isFetching;
  const porLinha = React.useMemo(
    () => new Map((conferir.data?.linhas ?? []).map((l) => [l.linha, l])),
    [conferir.data],
  );

  /** Cada linha lida, com o que o servidor disse dela. */
  const linhas = lidas.map((l) => {
    const c = porLinha.get(l.linha) ?? null;
    const descartada = descartadas.has(l.linha);
    const situacao: 'cabecalho' | 'descartada' | 'invalida' | 'problema' | 'ok' | 'conferindo' =
      l.cabecalho
        ? 'cabecalho'
        : descartada
          ? 'descartada'
          : l.problema
            ? 'invalida'
            : !c
              ? 'conferindo'
              : c.impedimentos.length > 0
                ? 'problema'
                : 'ok';
    return { lida: l, c, situacao };
  });

  const prontas = linhas.filter((l) => l.situacao === 'ok');
  const comProblema = linhas.filter((l) => l.situacao === 'invalida' || l.situacao === 'problema');
  const unidades = prontas.reduce((a, l) => a + (l.c?.quantidade ?? 0), 0);
  const tudoPronto =
    podeMover &&
    !conferindo &&
    !conferir.isPlaceholderData &&
    prontas.length > 0 &&
    comProblema.length === 0 &&
    linhas.every((l) => l.situacao !== 'conferindo');

  const gravar = useMutation({
    mutationFn: () =>
      api.post<{ transferencia: number; itens: number; unidades: number }>(
        '/api/transferencias-localizador',
        {
          centro,
          observacao,
          itens: prontas.map(({ c }) => ({
            produto: c!.produto!.produto,
            origem: c!.origem!.localizador,
            destino: c!.destino!.localizador,
            quantidade: c!.quantidade,
            ean: c!.codigo,
          })),
          importacao: {
            arquivo,
            texto: textoAtrasado,
            linhas: linhas.map(({ lida, c, situacao }) => ({
              linha: lida.linha,
              texto: lida.texto,
              situacao:
                situacao === 'ok' || situacao === 'cabecalho' || situacao === 'descartada'
                  ? situacao
                  : 'invalida',
              motivo: lida.problema ?? c?.impedimentos[0] ?? null,
            })),
          },
        },
      ),
    onSuccess: async (r) => {
      definirGravada(r);
      definirErro(null);
      definirConfirmando(false);
      definirTexto('');
      definirArquivo(null);
      definirObservacao('');
      await cliente.invalidateQueries({ queryKey: ['lote-localizador-historico'] });
    },
    onError: (e) => {
      definirConfirmando(false);
      definirErro(e instanceof Error ? e.message : 'Não foi possível gravar.');
      void conferir.refetch();
    },
  });

  const adicionarBipe = (): void => {
    // Código, EAN ou o código do lote (alfanumérico, `V000…`) — o que o coletor aceita.
    const codigo = bipe.replace(/[^0-9A-Za-z]/g, '');
    if (codigo === '') return;
    const q = Number(bipeQtde) > 0 ? Number(bipeQtde) : 1;
    definirTexto((t) => `${t}${t === '' || t.endsWith('\n') ? '' : '\n'}${codigo};${q}\n`);
    definirBipe('');
    definirBipeQtde('1');
    definirGravada(null);
    campoBipe.current?.focus();
  };

  const receberArquivo = async (f: File): Promise<void> => {
    if (/\.(xlsx?|ods)$/i.test(f.name)) {
      definirAviso('Planilha: selecione as células no Excel, copie (Ctrl+C) e cole na caixa.');
      return;
    }
    definirAviso(null);
    definirArquivo(f.name);
    definirGravada(null);
    definirTexto(await lerArquivo(f));
  };

  const padroes = conferir.data?.padroes;

  return (
    <Cartao className="divide-y divide-borda">
      {gravada ? (
        <div className="flex flex-wrap items-center gap-2.5 bg-sucesso/10 px-4 py-3 text-[13px] text-sucesso">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span className="font-semibold">
            Transferência {gravada.transferencia} gravada: {numero(gravada.itens)} item(ns),{' '}
            {numero(gravada.unidades)} un. O estoque já mudou de lugar.
          </span>
          <Botao
            variante="secundaria"
            tamanho="micro"
            className="ml-auto"
            onClick={() => aoVerHistorico(gravada.transferencia)}
          >
            Ver detalhe
          </Botao>
        </div>
      ) : null}

      {/* 1. de onde, para onde */}
      <section className="space-y-2 px-4 py-3.5">
        <Passo n={1} titulo="De onde sai e para onde vai">
          vale para as linhas que não trazem o seu localizador
        </Passo>
        {/*
          A ROTA, e não dois campos: sai de → vai para. O input é só o código
          (pequeno); o que salta é a descrição do localizador achado, que é o
          que a pessoa confere com o olho na etiqueta da prateleira.
        */}
        <div className="grid items-stretch gap-2 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
          <CampoLocalizador
            ponta="origem"
            valor={origem}
            aoMudar={definirOrigem}
            resolvido={origem.trim() === origemAtrasada ? (padroes?.origem ?? null) : null}
            carregando={origem.trim() !== '' && (origem.trim() !== origemAtrasada || conferir.isFetching)}
          />
          <div className="flex items-center justify-center gap-1 sm:flex-col">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-primaria text-white shadow-xs">
              <ArrowRight className="h-5 w-5 rotate-90 sm:rotate-0" />
            </span>
            <Botao
              variante="fantasma"
              tamanho="micro"
              onClick={() => {
                definirOrigem(destino);
                definirDestino(origem);
              }}
              aria-label="Inverter origem e destino"
              title="Inverter origem e destino"
            >
              <ArrowLeftRight className="h-3.5 w-3.5" />
              inverter
            </Botao>
          </div>
          <CampoLocalizador
            ponta="destino"
            valor={destino}
            aoMudar={definirDestino}
            resolvido={destino.trim() === destinoAtrasado ? (padroes?.destino ?? null) : null}
            carregando={destino.trim() !== '' && (destino.trim() !== destinoAtrasado || conferir.isFetching)}
          />
        </div>
      </section>

      {/* 2. os produtos */}
      <section className="space-y-2.5 px-4 py-3.5">
        <Passo n={2} titulo="Produtos">
          bipe, cole do Excel ou abra um .csv/.txt — tudo cai na mesma lista
        </Passo>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <ScanLine className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-textoFraco" />
            <input
              ref={campoBipe}
              value={bipe}
              onChange={(e) => definirBipe(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  adicionarBipe();
                }
              }}
              placeholder="Bipe ou digite o código, EAN ou lote e Enter"
              aria-label="Código ou EAN do produto"
              className="min-h-[38px] w-full rounded-lg border border-borda bg-superficie pl-8 pr-2 font-mono text-[13px] focus:border-primaria focus:outline-none focus:ring-4 focus:ring-primaria/12"
            />
          </div>
          <input
            value={bipeQtde}
            onChange={(e) => definirBipeQtde(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                adicionarBipe();
              }
            }}
            inputMode="numeric"
            aria-label="Quantidade do bipe"
            title="Quantidade"
            className="tabular min-h-[38px] w-[70px] rounded-lg border border-borda bg-superficie px-2 text-center font-mono text-[13px]"
          />
          <Botao variante="secundaria" tamanho="pequeno" onClick={adicionarBipe} disabled={bipe.trim() === ''}>
            <Plus className="h-3.5 w-3.5" />
            Adicionar
          </Botao>
        </div>
        {bipe !== '' && (origem.trim() === '' || destino.trim() === '') ? (
          <p className="text-[11.5px] text-atencao">
            Para bipar só o produto, preencha a origem e o destino no passo 1.
          </p>
        ) : null}

        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files[0];
            if (f) void receberArquivo(f);
          }}
        >
          <textarea
            value={texto}
            onChange={(e) => {
              definirTexto(e.target.value);
              definirGravada(null);
              if (e.target.value === '') definirArquivo(null);
            }}
            rows={6}
            placeholder={
              'Uma linha por produto. Duas formas:\n' +
              '  12745;3                  → usa a origem e o destino do passo 1\n' +
              '  12745;52109;41533;3      → produto;origem;destino;qtde\n\n' +
              'Localizador é o CÓDIGO (ex.: 52109). TAB do Excel, ; ou espaço separam.'
            }
            aria-label="Lista de produtos"
            className="w-full rounded-lg border border-borda bg-superficie p-2.5 font-mono text-[13px] focus:border-primaria focus:outline-none focus:ring-4 focus:ring-primaria/12"
          />
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11.5px] text-textoFraco">
            <input
              ref={seletorArquivo}
              type="file"
              accept=".txt,.csv,text/plain,text/csv"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void receberArquivo(f);
                e.target.value = '';
              }}
            />
            <Botao variante="secundaria" tamanho="micro" onClick={() => seletorArquivo.current?.click()}>
              <Upload className="h-3.5 w-3.5" />
              Abrir .csv / .txt
            </Botao>
            <Botao
              variante="fantasma"
              tamanho="micro"
              onClick={() => baixar('modelo-transferencia-localizadores.csv', MODELO_CSV)}
            >
              <Download className="h-3.5 w-3.5" />
              Baixar modelo
            </Botao>
            <span>ou solte o arquivo na caixa.</span>
            {arquivo ? <span className="font-mono">{arquivo}</span> : null}
            {texto !== '' ? (
              <Botao
                variante="fantasma"
                tamanho="micro"
                className="ml-auto"
                onClick={() => {
                  definirTexto('');
                  definirArquivo(null);
                }}
              >
                <X className="h-3.5 w-3.5" />
                Limpar lista
              </Botao>
            ) : null}
          </div>
          {aviso ? <div className="mt-1 text-[12px] text-atencao">{aviso}</div> : null}
        </div>
      </section>

      {/* 3. a conferência */}
      {lidas.length > 0 ? (
        <section className="space-y-2.5 px-4 py-3.5">
          <Passo n={3} titulo="Conferência">
            nada é gravado até você confirmar
          </Passo>

          {grande ? (
            <div className="rounded-lg border border-atencao/40 bg-atencao/10 px-3 py-2 text-[12.5px]">
              {numero(lidas.length)} linhas — o limite é {numero(MAX_LINHAS_LOTE)} por transferência.
              Divida a lista.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
                <span className="font-semibold text-sucesso">
                  <Check className="mr-0.5 inline h-3.5 w-3.5" />
                  {numero(prontas.length)} pronta(s) · {numero(unidades)} un
                </span>
                {comProblema.length > 0 ? (
                  <span className="font-semibold text-perigo">
                    {numero(comProblema.length)} com problema
                  </span>
                ) : null}
                {conferindo ? <span className="text-textoFraco">conferindo…</span> : null}
                {comProblema.length > 0 ? (
                  <Botao
                    variante="secundaria"
                    tamanho="micro"
                    className="ml-auto"
                    onClick={() =>
                      definirDescartadas((a) => {
                        const n = new Set(a);
                        for (const l of comProblema) n.add(l.lida.linha);
                        return n;
                      })
                    }
                  >
                    <X className="h-3.5 w-3.5" />
                    Tirar as {numero(comProblema.length)} com problema
                  </Botao>
                ) : null}
              </div>

              {conferir.isError ? (
                <div className="text-[12.5px] text-perigo">
                  Não foi possível conferir:{' '}
                  {conferir.error instanceof Error ? conferir.error.message : 'erro'}
                </div>
              ) : null}

              <TabelaConferencia
                linhas={linhas}
                escolhas={escolhas}
                aoEscolher={(linha, produto) => definirEscolhas((a) => ({ ...a, [linha]: produto }))}
                aoAlternar={(linha) =>
                  definirDescartadas((a) => {
                    const n = new Set(a);
                    if (n.has(linha)) n.delete(linha);
                    else n.add(linha);
                    return n;
                  })
                }
              />
            </>
          )}
        </section>
      ) : null}

      {/* rodapé: observação e o botão */}
      <section className="sticky bottom-0 z-10 space-y-2 rounded-b-xl bg-superficie px-4 py-3">
        {erro ? (
          <div className="flex items-start gap-2 rounded-lg border border-perigo/40 bg-perigo/10 px-3 py-2 text-[12.5px]">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-perigo" />
            <span>{erro}</span>
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={observacao}
            onChange={(e) => definirObservacao(e.target.value)}
            placeholder="Observação (opcional) — ex.: reorganização da rua MC002"
            aria-label="Observação"
            maxLength={500}
            className="min-h-[36px] min-w-[220px] flex-1 rounded-lg border border-borda bg-superficie px-3 text-[13px]"
          />
          {confirmando ? (
            <span className="inline-flex flex-wrap items-center gap-1.5">
              <span className="text-[12px] font-semibold text-atencao">
                Move {numero(unidades)} un no centro {centro}
                {centroNome ? ` · ${centroNome}` : ''} agora, sem aprovação. Confirma?
              </span>
              <Botao
                variante="primaria"
                tamanho="pequeno"
                carregando={gravar.isPending}
                onClick={() => gravar.mutate()}
              >
                Confirmar
              </Botao>
              <Botao variante="fantasma" tamanho="pequeno" onClick={() => definirConfirmando(false)}>
                Voltar
              </Botao>
            </span>
          ) : (
            <Botao
              variante="primaria"
              tamanho="pequeno"
              disabled={!tudoPronto}
              onClick={() => {
                definirErro(null);
                definirConfirmando(true);
              }}
              title={
                !podeMover
                  ? 'Você não tem a permissão estoque.mover'
                  : comProblema.length > 0
                    ? 'Resolva ou tire as linhas com problema'
                    : undefined
              }
            >
              <ArrowRight className="h-4 w-4" />
              Transferir {prontas.length > 0 ? `${numero(prontas.length)} item(ns)` : ''}
            </Botao>
          )}
        </div>
        {!podeMover ? (
          <p className="text-[11.5px] text-textoFraco">
            Você pode montar e conferir a lista, mas gravar exige a permissão estoque.mover.
          </p>
        ) : null}
      </section>
    </Cartao>
  );
}

function Passo({
  n,
  titulo,
  children,
}: {
  n: number;
  titulo: string;
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2">
      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primaria-suave text-[11px] font-bold text-primaria">
        {n}
      </span>
      <span className="text-[13px] font-semibold">{titulo}</span>
      {children ? <span className="text-[11.5px] text-textoFraco">{children}</span> : null}
    </div>
  );
}

function SetaTransferencia(): JSX.Element {
  return <ArrowLeftRight className="mt-0.5 h-4 w-4 text-primaria" aria-hidden="true" />;
}

function TabelaConferencia({
  linhas,
  escolhas,
  aoEscolher,
  aoAlternar,
}: {
  linhas: {
    lida: LinhaLote;
    c: LinhaConferida | null;
    situacao: 'cabecalho' | 'descartada' | 'invalida' | 'problema' | 'ok' | 'conferindo';
  }[];
  escolhas: Record<number, number>;
  aoEscolher: (linha: number, produto: number) => void;
  aoAlternar: (linha: number) => void;
}): JSX.Element {
  return (
    /* Scroll próprio: lista longa não empurra o botão para fora da tela. */
    <div className="max-h-[420px] overflow-auto rounded-lg border border-borda">
      <table className="w-full text-[12.5px]">
        <thead className="sticky top-0 z-[1] bg-superficie2 text-left text-[11px] uppercase tracking-wide text-textoFraco">
          <tr>
            <th className="px-2 py-1.5">Lin.</th>
            <th className="px-2 py-1.5">Produto</th>
            <th className="px-2 py-1.5" title="Localizador de origem e o saldo do produto nele">
              Sai de
            </th>
            <th className="w-6 px-0.5 py-1.5" aria-label="transferência" title="Transferência: sai de um, entra no outro">
              <ArrowLeftRight className="h-4 w-4 text-primaria" aria-hidden="true" />
            </th>
            <th className="px-2 py-1.5">Entra em</th>
            <th className="px-2 py-1.5 text-right">Qtde</th>
            <th className="w-8" />
          </tr>
        </thead>
        <tbody>
          {linhas.map(({ lida, c, situacao }) => {
            const fora = situacao === 'descartada' || situacao === 'cabecalho';
            const ruim = situacao === 'invalida' || situacao === 'problema';
            const motivos = situacao === 'invalida' ? [lida.problema!] : (c?.impedimentos ?? []);
            return (
              <React.Fragment key={lida.linha}>
                <tr
                  className={cn(
                    'border-t border-borda align-top',
                    fora && 'opacity-50',
                    ruim && 'bg-perigo/[0.05]',
                  )}
                >
                  <td className="tabular px-2 py-1.5 font-mono text-textoFraco">
                    {situacao === 'ok' ? (
                      <Check className="inline h-3.5 w-3.5 text-sucesso" />
                    ) : ruim ? (
                      <AlertTriangle className="inline h-3.5 w-3.5 text-perigo" />
                    ) : null}{' '}
                    {lida.linha}
                  </td>
                  <td className="max-w-[280px] px-2 py-1.5">
                    {situacao === 'cabecalho' ? (
                      <span className="text-textoFraco">cabeçalho — ignorado</span>
                    ) : c && c.candidatos.length > 1 && situacao !== 'descartada' ? (
                      <select
                        value={escolhas[lida.linha] ?? ''}
                        onChange={(e) => aoEscolher(lida.linha, Number(e.target.value))}
                        aria-label={`Produto da linha ${lida.linha}`}
                        className={cn(
                          'w-full rounded-md border bg-superficie px-1.5 py-1 text-[12.5px]',
                          c.produto ? 'border-borda' : 'border-atencao',
                        )}
                      >
                        <option value="">{lida.codigo} é ambíguo — qual produto?</option>
                        {c.candidatos.map((x) => (
                          <option key={x.produto} value={x.produto}>
                            {x.produto} · {x.descricao ?? 'sem descrição'} (
                            {x.via === 'ean' ? 'pelo EAN' : x.via === 'lote' ? 'pelo lote' : 'pelo código'})
                          </option>
                        ))}
                      </select>
                    ) : c?.produto ? (
                      <span className="flex min-w-0 items-center gap-1.5">
                        <span className="tabular shrink-0 font-mono font-bold">{c.produto.produto}</span>
                        <span className="truncate" title={c.produto.descricao ?? ''}>
                          {c.produto.descricao ?? '—'}
                        </span>
                      </span>
                    ) : (
                      <span className="font-mono" title={lida.texto}>
                        {lida.codigo ?? lida.texto}
                      </span>
                    )}
                  </td>
                  <td className="max-w-[190px] px-2 py-1.5">
                    <Localizador
                      ponta="origem"
                      p={c?.origem ?? null}
                      textoCru={lida.origem}
                      extra={
                        c && c.saldoOrigem !== null ? (
                          <span
                            className={cn(
                              'ml-1.5',
                              c.totalNaOrigem > c.saldoOrigem && 'font-bold text-perigo',
                            )}
                            title={
                              c.totalNaOrigem > c.quantidade
                                ? `A lista tira ${c.totalNaOrigem} deste produto desta origem, somando as linhas`
                                : 'Saldo do produto neste localizador'
                            }
                          >
                            · saldo {numero(c.saldoOrigem)}
                            {c.totalNaOrigem > c.quantidade ? ` (lista tira ${numero(c.totalNaOrigem)})` : ''}
                          </span>
                        ) : null
                      }
                    />
                  </td>
                  <td className="px-0.5 py-1.5">
                    <SetaTransferencia />
                  </td>
                  <td className="max-w-[190px] px-2 py-1.5">
                    <Localizador ponta="destino" p={c?.destino ?? null} textoCru={lida.destino} />
                  </td>
                  <td className="tabular px-2 py-1.5 text-right font-mono font-bold">
                    {lida.quantidade === null ? '—' : numero(lida.quantidade)}
                  </td>
                  <td className="px-1 py-1">
                    {situacao !== 'cabecalho' ? (
                      <button
                        type="button"
                        onClick={() => aoAlternar(lida.linha)}
                        className="rounded p-1 text-textoFraco hover:bg-superficie2 hover:text-texto"
                        aria-label={
                          situacao === 'descartada'
                            ? `Voltar a linha ${lida.linha}`
                            : `Tirar a linha ${lida.linha}`
                        }
                        title={situacao === 'descartada' ? 'voltar a linha' : 'tirar da transferência'}
                      >
                        {situacao === 'descartada' ? (
                          <Undo2 className="h-3.5 w-3.5" />
                        ) : (
                          <X className="h-3.5 w-3.5" />
                        )}
                      </button>
                    ) : null}
                  </td>
                </tr>
                {!fora && (motivos.length > 0 || (c?.avisos.length ?? 0) > 0) ? (
                  <tr className={cn(ruim && 'bg-perigo/[0.05]')}>
                    <td />
                    <td colSpan={6} className="px-2 pb-1.5 text-[11.5px]">
                      {motivos.map((m) => (
                        <span key={m} className="mr-3 inline-block text-perigo">
                          {m}
                        </span>
                      ))}
                      {(c?.avisos ?? []).map((m) => (
                        <span key={m} className="mr-3 inline-block text-atencao">
                          {m}
                        </span>
                      ))}
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Histórico
// ---------------------------------------------------------------------------

function HistoricoLote({
  centro,
  aberta,
  abrir,
  usarLista,
}: {
  centro: number;
  aberta: number | null;
  abrir: (n: number | null) => void;
  usarLista: (texto: string) => void;
}): JSX.Element {
  const lista = useQuery<{ transferencias: Resumo[] }>({
    queryKey: ['lote-localizador-historico', centro],
    queryFn: () =>
      api.get<{ transferencias: Resumo[] }>('/api/transferencias-localizador', { centro, dias: 30 }),
    staleTime: 30_000,
  });
  const linhas = lista.data?.transferencias ?? [];

  return (
    <div className="grid items-start gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]">
      <Cartao className="flex max-h-[calc(100vh-220px)] min-h-[240px] flex-col">
        <div className="flex items-baseline gap-2 border-b border-borda px-4 py-2.5">
          <span className="text-[13px] font-bold">Últimos 30 dias</span>
          <span className="text-[11.5px] text-textoFraco">
            {lista.isLoading ? '' : `${numero(linhas.length)} transferência(s) — WMS e Procfit`}
          </span>
          <Botao
            variante="fantasma"
            tamanho="micro"
            className="ml-auto"
            onClick={() => void lista.refetch()}
            carregando={lista.isFetching}
            aria-label="Atualizar"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Botao>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {lista.isLoading ? (
            <Esqueleto className="m-4 h-48" />
          ) : lista.isError ? (
            <EstadoErro erro={lista.error} aoTentarDeNovo={() => void lista.refetch()} />
          ) : linhas.length === 0 ? (
            <EstadoVazio
              icone={<History className="h-7 w-7" />}
              titulo="Nenhuma transferência nos últimos 30 dias"
              detalhe="Monte uma em “Nova transferência”."
            />
          ) : (
            <ul className="divide-y divide-borda">
              {linhas.map((l) => (
                <li key={l.transferencia}>
                  <button
                    type="button"
                    onClick={() => abrir(l.transferencia)}
                    className={cn(
                      'flex w-full items-center gap-3 border-l-[3px] px-4 py-2 text-left hover:bg-superficie2',
                      aberta === l.transferencia
                        ? 'border-l-primaria bg-primaria-suave/40'
                        : 'border-l-transparent',
                    )}
                  >
                    <span className="tabular w-[64px] shrink-0 font-mono text-[13px] font-bold">
                      {l.transferencia}
                    </span>
                    <span className="min-w-0 flex-1 leading-tight">
                      <span className="block truncate text-[12px]">
                        {l.usuarioNome ?? l.usuario ?? '—'}
                      </span>
                      <span className="block truncate text-[11px] text-textoFraco">
                        {dataHora(l.data)}
                        {l.observacao ? ` · ${l.observacao}` : ''}
                      </span>
                    </span>
                    <span className="tabular shrink-0 text-right text-[12px]">
                      <strong>{numero(l.itens)}</strong>
                      <span className="text-textoFraco"> itens</span>
                      <span className="block text-[11px] text-textoFraco">{numero(l.unidades)} un</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Cartao>

      <div className="lg:sticky lg:top-3">
        {aberta ? (
          <DetalheLote transferencia={aberta} aoFechar={() => abrir(null)} usarLista={usarLista} />
        ) : (
          <Cartao className="grid min-h-[200px] place-items-center px-6 text-center text-[13px] text-textoFraco">
            Clique numa transferência para ver os itens.
          </Cartao>
        )}
      </div>
    </div>
  );
}

/** Os itens no formato que a lista lê de volta: produto;origem;destino;qtde. */
function listaDosItens(itens: ItemDetalhe[], inverter = false): string {
  return (
    'produto;localizador_origem;localizador_destino;quantidade\r\n' +
    itens
      .map((i) =>
        inverter
          ? `${i.produto};${i.destino};${i.origem};${i.quantidade}`
          : `${i.produto};${i.origem};${i.destino};${i.quantidade}`,
      )
      .join('\r\n') +
    '\r\n'
  );
}

function DetalheLote({
  transferencia,
  aoFechar,
  usarLista,
}: {
  transferencia: number;
  aoFechar: () => void;
  usarLista: (texto: string) => void;
}): JSX.Element {
  const [copiado, definirCopiado] = React.useState(false);
  const detalhe = useQuery<Detalhe>({
    queryKey: ['lote-localizador-detalhe', transferencia],
    queryFn: () => api.get<Detalhe>(`/api/transferencias-localizador/${transferencia}`),
    staleTime: 60_000,
  });
  const d = detalhe.data;

  const copiar = async (): Promise<void> => {
    if (!d) return;
    try {
      await navigator.clipboard.writeText(listaDosItens(d.itensDetalhe));
      definirCopiado(true);
      window.setTimeout(() => definirCopiado(false), 2000);
    } catch {
      // Sem área de transferência (http): o .csv continua ao lado.
    }
  };

  return (
    <Cartao className="flex max-h-[calc(100vh-220px)] min-h-[240px] flex-col">
      <div className="flex items-start gap-2 border-b border-borda px-4 py-2.5">
        <span className="leading-tight">
          <span className="rotulo block">Transferência entre localizadores</span>
          <span className="tabular font-mono text-[16px] font-bold">{transferencia}</span>
        </span>
        <Botao variante="fantasma" tamanho="micro" className="ml-auto" onClick={aoFechar} aria-label="Fechar">
          <X className="h-3.5 w-3.5" />
        </Botao>
      </div>
      {detalhe.isLoading ? (
        <Esqueleto className="m-4 h-48" />
      ) : detalhe.isError || !d ? (
        <EstadoErro erro={detalhe.error} aoTentarDeNovo={() => void detalhe.refetch()} />
      ) : (
        <>
          <div className="space-y-1.5 border-b border-borda px-4 py-2.5 text-[12px]">
            <div className="flex flex-wrap gap-x-4 gap-y-0.5">
              <span>
                <span className="text-textoFraco">Quando </span>
                <strong>{dataHora(d.data)}</strong>
              </span>
              <span>
                <span className="text-textoFraco">Quem </span>
                <strong>{d.usuarioNome ?? d.usuario ?? '—'}</strong>
              </span>
              <span>
                <strong>{numero(d.itens)}</strong> itens · <strong>{numero(d.unidades)}</strong> un
              </span>
              <span className={d.lancamentos === d.itens * 2 ? 'text-sucesso' : 'text-atencao'}>
                kardex: {numero(d.lancamentos)} lançamentos
                {d.lancamentos === d.itens * 2 ? ' ✓' : ` (esperado ${d.itens * 2})`}
              </span>
            </div>
            {d.observacao ? <p className="text-textoFraco">{d.observacao}</p> : null}
            <div className="flex flex-wrap gap-1.5 pt-1">
              <Botao variante="secundaria" tamanho="micro" onClick={() => void copiar()}>
                {copiado ? <Check className="h-3.5 w-3.5 text-sucesso" /> : <Copy className="h-3.5 w-3.5" />}
                {copiado ? 'Copiado' : 'Copiar lista'}
              </Botao>
              <Botao
                variante="secundaria"
                tamanho="micro"
                onClick={() => baixar(`transferencia-localizador-${transferencia}.csv`, listaDosItens(d.itensDetalhe))}
              >
                <Download className="h-3.5 w-3.5" />
                Baixar .csv
              </Botao>
              <Botao
                variante="fantasma"
                tamanho="micro"
                onClick={() => usarLista(listaDosItens(d.itensDetalhe))}
                title="Abre esta lista numa nova transferência, para conferir e gravar"
              >
                <Repeat className="h-3.5 w-3.5" />
                Repetir
              </Botao>
              <Botao
                variante="fantasma"
                tamanho="micro"
                onClick={() => usarLista(listaDosItens(d.itensDetalhe, true))}
                title="Monta a lista com origem e destino trocados — para desfazer. Nada é gravado sem conferir."
              >
                <Undo2 className="h-3.5 w-3.5" />
                Montar o inverso
              </Botao>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full text-[12.5px]">
              <thead className="sticky top-0 bg-superficie2 text-left text-[11px] uppercase tracking-wide text-textoFraco">
                <tr>
                  <th className="px-3 py-1.5">Produto</th>
                  <th className="px-2 py-1.5">Saiu de</th>
                  <th className="w-6 px-0.5 py-1.5" aria-label="transferência" title="Transferência: sai de um, entra no outro">
                    <ArrowLeftRight className="h-4 w-4 text-primaria" aria-hidden="true" />
                  </th>
                  <th className="px-2 py-1.5">Entrou em</th>
                  <th className="px-3 py-1.5 text-right">Qtde</th>
                </tr>
              </thead>
              <tbody>
                {d.itensDetalhe.map((i) => (
                  <tr key={i.item} className="border-t border-borda align-top">
                    <td className="max-w-[240px] px-3 py-1.5">
                      <span className="tabular font-mono font-bold">{i.produto}</span>
                      <span className="block truncate text-[11px] text-textoFraco" title={i.descricao ?? ''}>
                        {i.descricao ?? '—'}
                      </span>
                    </td>
                    <td className="max-w-[170px] px-2 py-1.5">
                      <Localizador
                        ponta="origem"
                        p={{ localizador: i.origem, endereco: i.origemEndereco || String(i.origem), ativo: true }}
                      />
                    </td>
                    <td className="px-0.5 py-1.5">
                      <SetaTransferencia />
                    </td>
                    <td className="max-w-[170px] px-2 py-1.5">
                      <Localizador
                        ponta="destino"
                        p={{ localizador: i.destino, endereco: i.destinoEndereco || String(i.destino), ativo: true }}
                      />
                    </td>
                    <td className="tabular px-3 py-1.5 text-right font-mono font-bold">
                      {numero(i.quantidade)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {d.importacao ? (
              <details className="border-t border-borda px-4 py-2 text-[12px]">
                <summary className="cursor-pointer text-textoFraco">
                  <ClipboardPaste className="mr-1 inline h-3.5 w-3.5" />
                  Lista colada/importada
                  {d.importacao.arquivo ? ` (${d.importacao.arquivo})` : ''} — {d.importacao.login ?? ''}
                </summary>
                <pre className="mt-2 max-h-[240px] overflow-auto rounded-lg bg-superficie2 p-2 font-mono text-[11.5px]">
                  {d.importacao.texto}
                </pre>
              </details>
            ) : null}
          </div>
        </>
      )}
    </Cartao>
  );
}
